import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

// WebSocket URL configuration
// - Production: Use same origin (Vercel proxy) so cookies are sent correctly
// - Development: Empty string lets socket.io connect to the same host
// NOTE: Changed from direct Render URL to fix cookie/CSP issues
const WS_BASE_URL = process.env.NODE_ENV === 'production'
  ? undefined // Use same origin (Vercel will proxy /socket.io to Render)
  : '';

export interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  connecting: boolean;
  error: string | null;
  lastConnected: Date | null;
  reconnectAttempts: number;
  joinOrder: (orderId: string) => void;
  leaveOrder: (orderId: string) => void;
  subscribeToPackages: () => void;
  unsubscribeFromPackages: () => void;
  subscribeToWallet: () => void;
  subscribeToOrders: () => void;
  subscribeToAdminDashboard: () => void;
  subscribeToFraudEvents: (handler: (payload: any) => void) => () => void;
  subscribeToNotifications: () => void;
  subscribeToOrderTracking: (orderId: string) => void;
  disconnect: () => void;
  reconnect: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

// In both dev and production, use same host (proxy handles the routing)
const SOCKET_URL = WS_BASE_URL;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated, updateWalletBalance } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastConnected, setLastConnected] = useState<Date | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const updateWalletBalanceRef = useRef(updateWalletBalance);
  const userRef = useRef(user);

  useEffect(() => {
    updateWalletBalanceRef.current = updateWalletBalance;
  }, [updateWalletBalance]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    if (!isAuthenticated || !userRef.current?.id) {
      return;
    }

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnecting(true);
    setError(null);

    try {
      // NOTE: Using polling only in production because Vercel's serverless functions
      // don't support WebSocket proxying. Polling works reliably through the proxy.
      const transports = process.env.NODE_ENV === 'production'
        ? ['polling']  // Force polling only in production
        : ['websocket', 'polling'];  // Allow both in development
        
      const newSocket = io(SOCKET_URL, {
        withCredentials: true,
        transports,
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: RECONNECT_DELAY,
        timeout: 10000,
        autoConnect: true,
      });

      // Connection events
      newSocket.on('connect', () => {
        setConnected(true);
        setConnecting(false);
        setError(null);
        setLastConnected(new Date());
        setReconnectAttempts(0);

        // Join user's personal room for notifications
        if (userRef.current?.id) {
          newSocket.emit('join:user', userRef.current.id);
        }

        // Subscribe to role-based rooms
        if (userRef.current?.role === 'admin') {
          newSocket.emit('join:admin');
        }
      });

      newSocket.on('disconnect', (reason) => {
        setConnected(false);
        
        if (reason === 'io server disconnect') {
          // Server disconnected, try to reconnect
          setError('Server disconnected. Reconnecting...');
        } else if (reason === 'transport close') {
          setError('Connection lost. Reconnecting...');
        }
      });

      newSocket.on('connect_error', (err) => {
        setConnecting(false);
        setConnected(false);
        setError(`Connection failed: ${err.message}`);
        setReconnectAttempts(prev => prev + 1);
      });

      newSocket.on('reconnect', (attemptNumber) => {
        setConnected(true);
        setError(null);
        setReconnectAttempts(0);
        toast.success('Real-time connection restored');
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        setReconnectAttempts(attemptNumber);
      });

      newSocket.on('reconnect_failed', () => {
        setError('Unable to connect. Please refresh the page.');
        setConnecting(false);
      });

      // Handle authentication errors
      newSocket.on('error', (error: any) => {
        if (error.message === 'Authentication error') {
          setError('Authentication failed');
          newSocket.disconnect();
        }
      });

      // Handle wallet balance updates
      newSocket.on('wallet:balance-updated', (data: any) => {
        // Update auth context wallet balance
        if (data.balance !== undefined && updateWalletBalanceRef.current) {
          updateWalletBalanceRef.current(data.balance);
          
          // Show notification for balance changes
          if (data.amount && data.amount > 0) {
            toast.success(`Wallet credited: +GHS ${data.amount}`);
          } else if (data.amount && data.amount < 0) {
            toast.error(`Wallet debited: GHS ${Math.abs(data.amount)}`);
          }
        }
      });

      // Handle wallet transaction updates
      newSocket.on('wallet:transaction-updated', (data: any) => {
        // Update balance if included in transaction data
        if (data.balance !== undefined && updateWalletBalanceRef.current) {
          updateWalletBalanceRef.current(data.balance);
        }
        
        // Show transaction notification
        if (data.status === 'completed') {
          if (data.type === 'credit') {
            toast.success(`Payment received: +GHS ${data.amount}`);
          } else if (data.type === 'debit') {
            toast(`Payment processed: -GHS ${data.amount}`);
          }
        }
      });

      // Handle legacy wallet_balance_update event (from config/socket.js)
      newSocket.on('wallet_balance_update', (data: any) => {
        if (data.balance !== undefined && updateWalletBalanceRef.current) {
          updateWalletBalanceRef.current(data.balance);
        }
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

    } catch (err: any) {

      setError(err.message || 'Failed to initialize socket');
      setConnecting(false);
    }
  }, [isAuthenticated]);

  // Initialize on auth change
  useEffect(() => {
    const reconnectTimeout = reconnectTimeoutRef.current;

    if (isAuthenticated) {
      initializeSocket();
    } else {
      // Disconnect when logged out
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
    }

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [isAuthenticated, initializeSocket]);

  // Socket action methods
  const joinOrder = useCallback((orderId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join:order', orderId);
    }
  }, []);

  const leaveOrder = useCallback((orderId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave:order', orderId);
    }
  }, []);

  const subscribeToPackages = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe:packages');
    }
  }, []);

  const unsubscribeFromPackages = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe:packages');
    }
  }, []);

  const subscribeToWallet = useCallback(() => {
    if (socketRef.current?.connected && user?.id) {
      socketRef.current.emit('subscribeWallet');
      socketRef.current.emit('subscribe:wallet', user.id);
    }
  }, [user]);

  const subscribeToOrders = useCallback(() => {
    if (socketRef.current?.connected && user?.id) {
      socketRef.current.emit('subscribe:orders', user.id);
    }
  }, [user]);

  const subscribeToAdminDashboard = useCallback(() => {
    if (socketRef.current?.connected && user?.role === 'admin') {
      socketRef.current.emit('subscribe:admin-dashboard');
    }
  }, [user]);

  const subscribeToFraudEvents = useCallback((handler: (payload: any) => void) => {
    const s = socketRef.current;
    if (!s || !userRef.current || userRef.current.role !== 'admin') {
      return () => {};
    }

    const wrapped = (payload: any) => {
      handler(payload);
    };

    s.on('NEW_FRAUD_EVENT', wrapped);

    return () => {
      s.off('NEW_FRAUD_EVENT', wrapped);
    };
  }, []);

  const subscribeToNotifications = useCallback(() => {
    if (socketRef.current?.connected && user?.id) {
      socketRef.current.emit('subscribe:notifications', user.id);
    }
  }, [user]);

  const subscribeToOrderTracking = useCallback((orderId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe:order-tracking', orderId);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      setConnected(false);
    }
  }, []);

  const reconnect = useCallback(() => {
    if (!socketRef.current?.connected) {
      setReconnectAttempts(0);
      initializeSocket();
    }
  }, [initializeSocket]);

  const value: SocketContextType = {
    socket,
    connected,
    connecting,
    error,
    lastConnected,
    reconnectAttempts,
    joinOrder,
    leaveOrder,
    subscribeToPackages,
    unsubscribeFromPackages,
    subscribeToWallet,
    subscribeToOrders,
    subscribeToAdminDashboard,
    subscribeToFraudEvents,
    subscribeToNotifications,
    subscribeToOrderTracking,
    disconnect,
    reconnect,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
